import { on, reducer } from "ts-action";
import { withLoadingReducer } from "../../../Components/Pickup/redux-store/reducers/withLoadingState";
import { getGiftGroupsAction } from "../../../constants";
import {
  addEmptyGiftGroup,
  addEmptyGiftGroupItem,
  addLoyaltySpecialItemSuccess,
  getGiftGroupsSuccess,
  modifyGiftGroups,
  modifyGiftGroupsFailure,
  modifyGiftGroupsSuccess,
  removeGiftGroupItem,
  setGiftGroupsCurrency,
  updateGiftGroupItems,
  updateGiftGroupTitle,
} from "../../actions";
import { GiftGroup, GiftItem } from "../../../types";
import { v4 as uuidv4 } from "uuid";
import { CURRENCIES, mapById } from "../../../Utils";
import { getMedianPrice } from "../../../helpers/gift-list/getMedianPrice";
import { emptyGiftItem } from "../../../helpers/gift-list/emptyGiftItem";
import { addGiftGroupItem } from "../../../helpers/gift-list/addGiftGroupItem";

export type GiftGroupsState = {
  loading: boolean;
  giftGroupsIds: string[];
  giftGroupsById: Record<string, GiftGroup>;
  shownGiftGroupsIds: string[];
  shownGiftGroupsById: Record<string, GiftGroup>;
  currency: {
    en: string;
    ar: string;
  };
};

const initialState: GiftGroupsState = {
  giftGroupsIds: [],
  giftGroupsById: {},
  shownGiftGroupsIds: [],
  shownGiftGroupsById: {},
  loading: false,
  currency: {
    en: "",
    ar: "",
  },
};

export default withLoadingReducer<GiftGroupsState>(
  reducer<GiftGroupsState>(
    [
      on(setGiftGroupsCurrency, (state, { payload }) => ({
        ...state,
        currency: {
          en: CURRENCIES["en"][payload.country_iso_code],
          ar: CURRENCIES["ar"][payload.country_iso_code],
        },
      })),
      on(getGiftGroupsSuccess, (state, { payload }) => {
        if (
          payload.length === 0 ||
          payload.every((group) => group.items.length === 0)
        ) {
          const group = {
            id: uuidv4(),
            name: `Gift - X ${state.currency.en}`,
            ar_name: `هدية - X ${state.currency.ar}`,
            price: 0,
            items: [{ ...emptyGiftItem(1), show_price: 0, is_placeholder: 1 }],
            is_placeholder: 1,
            is_renamed: 0,
            is_ar_renamed: 0,
          };

          return {
            ...state,
            giftGroupsIds: [group.id],
            shownGiftGroupsIds: [group.id],
            giftGroupsById: { [group.id]: group },
            shownGiftGroupsById: { [group.id]: group },
          };
        }

        return {
          ...state,
          giftGroupsIds: payload.map((group) => group.id),
          shownGiftGroupsIds: payload.map((group) => group.id),
          giftGroupsById: mapById(payload, "id"),
          shownGiftGroupsById: mapById(payload, "id"),
        };
      }),
      on(addEmptyGiftGroupItem, (state, { payload }) => ({
        ...state,
        shownGiftGroupsById: {
          ...state.shownGiftGroupsById,
          [payload.group_id]: {
            ...state.shownGiftGroupsById[payload.group_id],
            name: `Gift - ${getMedianPrice(
              state.shownGiftGroupsById[payload.group_id].items as GiftItem[]
            )} ${state.currency.en}`,
            ar_name: `هدية - ${getMedianPrice(
              state.shownGiftGroupsById[payload.group_id].items as GiftItem[]
            )} ${state.currency.ar}`,
            is_renamed: 0,
            is_ar_renamed: 0,
            items: [
              ...(state.shownGiftGroupsById[payload.group_id]
                .items as GiftItem[]),
              {
                ...emptyGiftItem(
                  state.shownGiftGroupsById[payload.group_id].items.length
                ),
                show_price: 0,
              },
            ],
          },
        },
      })),
      on(addEmptyGiftGroup, (state) => {
        const id = uuidv4();
        return {
          ...state,
          shownGiftGroupsIds: [...state.shownGiftGroupsIds, id],
          shownGiftGroupsById: {
            ...state.shownGiftGroupsById,
            [id]: {
              id,
              name: `Gift - X ${state.currency.en}`,
              ar_name: `هدية - X ${state.currency.ar}`,
              is_renamed: 0,
              is_ar_renamed: 0,
              items: [{ ...emptyGiftItem(1), show_price: 0 }],
              price: 0,
              is_placeholder: 1,
            },
          },
        };
      }),
      on(updateGiftGroupItems, (state, { payload }) => {
        const { group_id, item } = payload;
        const { state: newState } = addGiftGroupItem(state, {
          group_id,
          item,
        });
        if (!newState) return state;
        return { ...newState };
      }),
      on(removeGiftGroupItem, (state, { payload }) => {
        const { group_id, item } = payload;
        const group = state.shownGiftGroupsById[group_id];
        const filteredItems = (group.items as GiftItem[]).filter(
          (i) => i.id !== item.id
        );

        if (filteredItems.length === 0) {
          const { [group_id]: _, ...restById } = state.shownGiftGroupsById;
          const updatedIds = state.shownGiftGroupsIds.filter(
            (id) => id !== group_id
          );

          if (updatedIds.length === 0) {
            const placeholderId = uuidv4();
            return {
              ...state,
              shownGiftGroupsById: {
                [placeholderId]: {
                  id: placeholderId,
                  name: `Gift - X ${state.currency.en}`,
                  ar_name: `هدية - X ${state.currency.ar}`,
                  is_renamed: 0,
                  is_ar_renamed: 0,
                  items: [{ ...emptyGiftItem(1), show_price: 0 }],
                  price: 0,
                  is_placeholder: 1,
                },
              },
              shownGiftGroupsIds: [placeholderId],
            };
          }

          return {
            ...state,
            shownGiftGroupsById: restById,
            shownGiftGroupsIds: updatedIds,
          };
        }
        const updatedGroupPrice = getMedianPrice(filteredItems);
        return {
          ...state,
          shownGiftGroupsById: {
            ...state.shownGiftGroupsById,
            [group_id]: {
              ...group,
              name: `Gift - ${updatedGroupPrice} ${state.currency.en}`,
              ar_name: `هدية - ${updatedGroupPrice} ${state.currency.ar}`,
              is_ar_renamed: 0,
              price: updatedGroupPrice,
              items: filteredItems,
            },
          },
        };
      }),
      on(updateGiftGroupTitle, (state, { payload }) => {
        const { group_id, name, ar_name, is_ar_renamed } = payload;
        const group = state.shownGiftGroupsById[group_id];
        if (!group) return state;
        return {
          ...state,
          shownGiftGroupsById: {
            ...state.shownGiftGroupsById,
            [group_id]: {
              ...group,
              name: name,
              is_renamed: 1,
              ar_name: ar_name,
              is_ar_renamed: is_ar_renamed ?? 1,
            },
          },
        };
      }),
      on(addLoyaltySpecialItemSuccess, (state, { payload }) => {
        const { state: newState } = addGiftGroupItem(state, {
          group_id: "",
          item: {
            ...payload,
            image: payload.image || "",
            integration_id: payload.integration_id || "",
            last_sync_change: Date.now(),
            last_sync_time: Date.now(),
            en_description: "",
            description: "",
            created_at: Date.now(),
            ar_name: payload.name,
            ar_description: "",
            updated_at: Date.now(),
            pos_image_path: payload.image || "",
            en_name: payload.name,
            updated_first_time_pos: 0,
            order: 1,
          } as GiftItem,
        });
        if (!newState) return state;
        return { ...newState };
      }),
      on(modifyGiftGroups, (state) => ({
        ...state,
        loading: true,
      })),
      on(modifyGiftGroupsSuccess, (state) => ({
        ...state,
        loading: false,
        giftGroupsIds: state.shownGiftGroupsIds,
        giftGroupsById: state.shownGiftGroupsById,
      })),
      on(modifyGiftGroupsFailure, (state) => ({
        ...state,
        loading: false,
      })),
    ],
    initialState
  ),
  getGiftGroupsAction
);
